import React from 'react';
import { FileText, MessageSquare, Languages, Image, Video, Shield, Library, Brain, Check, Clock } from 'lucide-react';
import FadeIn from '../components/FadeIn';
import CTASection from '../components/CTASection';
import { SEOHead, createBreadcrumbSchema } from '../components/SEOHead';

const FeaturesPage = () => {
  const features = [
    {
      icon: FileText,
      title: '100+ Šablon',
      shortDesc: 'Přípravy, testy, pracovní listy, IVP a další',
      longDesc: 'Kompletní knihovna pedagogických šablon pokrývající všechny stupně vzdělávání — od mateřské školy přes základní až po střední školu.',
      benefits: [
        'Šablony pro všechny předměty a ročníky (MŠ, ZŠ, SŠ)',
        'IVP, přípravy na hodinu, didaktické testy, pracovní listy',
        'Slovní hodnocení žáků — individuální a personalizovaná',
        'Export do PDF a DOCX'
      ],
      useCases: [
        {
          title: 'Šablona: Příprava na hodinu',
          desc: 'Zadání: „Čeština, 3. třída, vyjmenovaná slova po B, 45 minut."\nVýstup: Návrh hodiny včetně aktivit a pracovního listu.',
          timeSaved: 'Hotovo do 5 minut'
        },
        {
          title: 'Šablona: Individuální vzdělávací plán',
          desc: 'Zadání: „5. třída, ADHD, pozornost, organizace práce, krátké instrukce."\nVýstup: Základ IVP s cíli, opatřeními a doporučeními.',
          timeSaved: 'Hotovo do 5 minut'
        }
      ],
      gradient: 'from-blue-50 to-cyan-50',
      iconBg: 'from-blue-500 to-cyan-500'
    },
    {
      icon: MessageSquare,
      title: 'AI Chat',
      shortDesc: 'Chat s AI modelem s okamžitým exportem do Word/PDF',
      longDesc: 'Klasický chat s AI modelem v češtině. Popište, co potřebujete, a AI vytvoří materiál. Výstup si jedním kliknutím stáhnete jako Word nebo PDF — bez kopírování a formátování.',
      benefits: [
        'Chat s AI modelem v české pedagogické terminologii',
        'Okamžité stažení výstupu do Word nebo PDF',
        'Možnost nahrát vlastní podklady',
        'Historie konverzací'
      ],
      useCases: [
        {
          title: 'Vytvoření pracovního listu',
          desc: 'Zadání: „Vytvoř pracovní list o fotosyntéze pro 5. ročník, 10 otázek + klíč."\nVýstup: Hotový pracovní list ke stažení jedním kliknutím.',
          timeSaved: 'Hotovo do 5 minut'
        },
        {
          title: 'Úprava existujícího materiálu',
          desc: 'Zadání: „Aktualizuj tento test podle nového RVP a přidej moderní příklady."\nVýstup: Upravený test připravený ke stažení.',
          timeSaved: 'Hotovo do 5 minut'
        }
      ],
      gradient: 'from-purple-50 to-pink-50',
      iconBg: 'from-purple-500 to-pink-500'
    },
    {
      icon: Brain,
      title: 'AI Asistenti',
      shortDesc: 'Pracují pouze s dokumenty vaší školy',
      longDesc: 'AI asistenti mají přístup pouze k dokumentům vaší školy — školnímu řádu, ŠVP a dalším materiálům, které nahrajete. Maximální bezpečnost a ochrana dat.',
      benefits: [
        'Přístup pouze k dokumentům vaší školy',
        'Plná kontrola nad přístupem',
        'GDPR compliant',
        'Šifrovaná komunikace',
        'Audit log aktivit'
      ],
      useCases: [
        {
          title: 'Práce se školním řádem',
          desc: 'Zadání: „Používej náš školní řád a připrav stručný přehled pro rodiče."\nVýstup: Srozumitelný text vycházející z pravidel naší školy.',
          timeSaved: 'Hotovo do 5 minut'
        },
        {
          title: 'Analýza školních dokumentů',
          desc: 'Zadání: „Projdi výroční zprávy školy a vypiš shody s konceptem rozvoje dle našeho ŠVP."\nVýstup: Přehled hlavních informací bez ručního pročítání.',
          timeSaved: 'Hotovo do 5 minut'
        }
      ],
      gradient: 'from-violet-50 to-purple-50',
      iconBg: 'from-violet-500 to-purple-500'
    },
    {
      icon: Languages,
      title: 'Překlady do 50+ jazyků',
      shortDesc: 'Překlady s pedagogickou terminologií',
      longDesc: 'Profesionální překlady s ohledem na pedagogický kontext. Překladač rozumí školské terminologii a zachovává význam a styl původního textu.',
      benefits: [
        'Více než 50 jazyků — všechny evropské + čínština, japonština',
        'Školská a pedagogická terminologie',
        'Automatická detekce jazyka'
      ],
      useCases: [
        {
          title: 'Materiály pro žáky OMJ',
          desc: 'Zadání: „Přelož tento pracovní list z přírodovědy do ukrajinštiny a vietnamštiny."\nVýstup: Přeložený pracovní list připravený pro žáky s OMJ.',
          timeSaved: 'Hotovo do 5 minut'
        },
        {
          title: 'Informace pro rodiče cizinců',
          desc: 'Zadání: „Přelož tuto zprávu pro rodiče do ukrajinštiny a angličtiny."\nVýstup: Přeložený text připravený pro komunikaci s rodiči.',
          timeSaved: 'Hotovo do 5 minut'
        }
      ],
      gradient: 'from-green-50 to-emerald-50',
      iconBg: 'from-green-500 to-emerald-500'
    },
    {
      icon: Image,
      title: 'Generování obrázků',
      shortDesc: 'Ilustrace a diagramy pro výuku',
      longDesc: 'Vytvářejte vlastní ilustrace a diagramy přesně podle vašich představ. Od jednoduchých schémat až po detailní ilustrace — vše na pár kliknutí.',
      benefits: [
        'Generování obrázků a ilustrací',
        'Různé styly: realistické, kreslené, diagramy',
        'Vysoké rozlišení vhodné i pro tisk'
      ],
      useCases: [
        {
          title: 'Ilustrace k povídce pro 2. třídu',
          desc: 'Zadání: „Vytvoř ilustraci k povídce o lese — kreslený styl, přátelská zvířátka."\nVýstup: Ilustrace připravená pro výuku.',
          timeSaved: 'Hotovo do 5 minut'
        },
        {
          title: 'Diagram pro chemii — SŠ',
          desc: 'Zadání: „Vygeneruj schéma fotosyntézy s popisky v češtině."\nVýstup: Diagram připravený pro prezentaci.',
          timeSaved: 'Hotovo do 5 minut'
        }
      ],
      gradient: 'from-orange-50 to-amber-50',
      iconBg: 'from-orange-500 to-amber-500'
    },
    {
      icon: Video,
      title: 'Inteligentní shrnutí videí',
      shortDesc: 'Nemusíte sledovat celé dlouhé video',
      longDesc: 'Nemusíte sledovat celé dlouhé video. Stačí vložit odkaz a Školobot z něj vytáhne to nejdůležitější do přehledného inteligentního shrnutí, které si přečtete během chvíle.',
      benefits: [
        'Inteligentní shrnutí videa',
        'Stačí vložit odkaz na video',
        'Klíčové myšlenky z videa v přehledné podobě',
        'Rychlé zorientování v dlouhém obsahu',
        'Úspora času při přípravě i studiu',
        'Přehledný textový výstup'
      ],
      useCases: [
        {
          title: 'Inteligentní shrnutí videa — školní praxe',
          desc: 'Zadání: „Shrň toto dvouhodinové video o formativním hodnocení do stručného přehledu pro pedagogy."\nVýstup: Stručné a přehledné textové shrnutí toho nejdůležitějšího bez nutnosti sledovat celé video.',
          timeSaved: 'Hotovo do 5 minut'
        }
      ],
      gradient: 'from-red-50 to-rose-50',
      iconBg: 'from-red-500 to-rose-500'
    },
    {
      icon: Shield,
      title: 'AI Detektor',
      shortDesc: 'Zjistěte, jestli žák opisoval z AI',
      longDesc: 'Nástroj pro detekci textů generovaných umělou inteligencí. Analyzuje styl, strukturu a slovní zásobu textu s přesností 0–100 %. Pomůže vám identifikovat, zda žák použil ChatGPT, Gemini nebo jiné AI nástroje.',
      benefits: [
        'Detekce AI-generovaného obsahu (ChatGPT, Gemini, Claude)',
        'Procentuální hodnocení s barevným zvýrazněním',
        'Podporuje češtinu, slovenštinu a další jazyky',
        'Podrobná analýza textu — stylistika, slovní zásoba, struktura',
        'Přehled všech provedených kontrol s možností exportu'
      ],
      useCases: [
        {
          title: 'Kontrola slohových prací',
          desc: 'Vložíte text slohové práce a AI detektor vyhodnotí pravděpodobnost, že text byl generován umělou inteligencí.',
          timeSaved: 'Hotovo do 5 minut'
        }
      ],
      gradient: 'from-indigo-50 to-violet-50',
      iconBg: 'from-indigo-500 to-violet-500'
    },
    {
      icon: Library,
      title: 'Knihovna & Historie',
      shortDesc: 'Vaše osobní úložiště všech materiálů',
      longDesc: 'Všechny materiály, které ve Školobotu vytvoříte, se automaticky ukládají do vaší osobní knihovny. Můžete se k nim kdykoliv vrátit, znovu je použít nebo upravit.',
      benefits: [
        'Automatické ukládání všech vygenerovaných materiálů',
        'Přehledná historie vašich generování',
        'Export kteréhokoliv materiálu do PDF nebo DOCX'
      ],
      useCases: [
        {
          title: 'Přístup k dřívějším materiálům',
          desc: 'Všechny vaše materiály jsou přehledně uložené na jednom místě. Stačí se přihlásit a máte k nim okamžitý přístup.',
          timeSaved: 'Vše na jednom místě'
        }
      ],
      gradient: 'from-teal-50 to-cyan-50',
      iconBg: 'from-teal-500 to-cyan-500'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-[color:var(--bg)] to-white">
      <SEOHead 
        title="Využití Školobota | Funkce AI nástroje pro pedagogy"
        description="Objevte všechny nástroje Školobota: 100+ šablon, AI chat asistent, generování obrázků, AI detektor, překlady do 50+ jazyků a podpora žáků se speciálními potřebami."
        path="/vyuziti"
        schema={createBreadcrumbSchema([{ name: 'Úvod', path: '/' }, { name: 'Využití', path: '/vyuziti' }])}
      />
      <section className="pt-28 pb-8 md:pt-32 md:pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <FadeIn>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[color:var(--text-primary)] mb-3">
              Využití Školobota
            </h1>
            <p className="text-base md:text-lg text-[color:var(--text-secondary)] max-w-2xl mx-auto">
              Objevte všechny nástroje, které vám pomohou ušetřit čas a zkvalitnit pedagogickou práci.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* Detailed Features */}
      <section className="pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto space-y-8">
          {features.map((feature, index) => {
            const Icon = feature.icon;
            const isEven = index % 2 === 0;
            
            return (
              <FadeIn key={index} delay={0}>
                <div className="bg-white border-2 border-[color:var(--primary-100)] rounded-2xl p-6 md:p-8 hover:border-[color:var(--primary-300)] hover:shadow-xl transition-all duration-300">
                  <div className={`grid grid-cols-1 lg:grid-cols-12 gap-6 items-start`}>
                    {/* Icon & Title - Compact */}
                    <div className="lg:col-span-4">
                      <div className={`bg-gradient-to-br ${feature.gradient} rounded-2xl p-6`}>
                        <div className={`w-16 h-16 rounded-xl bg-gradient-to-br ${feature.iconBg} flex items-center justify-center mb-4 shadow-lg`}>
                          <Icon className="w-8 h-8 text-white" />
                        </div>
                        <h3 className="text-2xl font-bold text-[color:var(--text-primary)] mb-2">
                          {feature.title}
                        </h3>
                        <p className="text-sm text-[color:var(--text-secondary)]">
                          {feature.shortDesc}
                        </p>
                      </div>
                    </div>

                    {/* Content - 2 columns */}
                    <div className="lg:col-span-8 grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Description & Benefits */}
                      <div>
                        <p className="text-sm text-[color:var(--text-secondary)] mb-4 leading-relaxed">
                          {feature.longDesc}
                        </p>
                        
                        <div className="space-y-2">
                          {feature.benefits.slice(0, 3).map((benefit, i) => (
                            <div key={i} className="flex items-start gap-2">
                              <Check className="w-4 h-4 text-[color:var(--primary-500)] flex-shrink-0 mt-0.5" />
                              <span className="text-xs text-[color:var(--text-primary)]">
                                {benefit}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Use Cases */}
                      <div>
                        {feature.benefits.slice(3).length > 0 && (
                          <div className="mb-4">
                            <div className="space-y-2">
                              {feature.benefits.slice(3).map((benefit, i) => (
                                <div key={i} className="flex items-start gap-2">
                                  <Check className="w-4 h-4 text-[color:var(--primary-500)] flex-shrink-0 mt-0.5" />
                                  <span className="text-xs text-[color:var(--text-primary)]">
                                    {benefit}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {feature.useCases && feature.useCases.length > 0 && (
                          <div className="space-y-3">
                            {feature.useCases.map((useCase, i) => (
                              <div key={i} className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-3">
                                <div className="font-bold text-[color:var(--text-primary)] text-sm mb-1">
                                  {useCase.title}
                                </div>
                                <p className="text-xs text-[color:var(--text-secondary)] mb-2">
                                  {useCase.desc}
                                </p>
                                <div className="flex items-center gap-1 text-green-700 font-bold text-xs">
                                  <Clock className="w-4 h-4" />
                                  {useCase.timeSaved}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </FadeIn>
            );
          })}
        </div>
      </section>

      <CTASection />
    </div>
  );
};

export default FeaturesPage;